import React, { useState } from "react";
import { Table, Button, Flex, Typography, notification, Grid } from "antd";
import { Tabs, Form, Modal } from "antd";
import { PlusOutlined, CloseCircleOutlined, EditOutlined } from "@ant-design/icons";
import {
	useGetGymEquipmentQuery,
	useGetDomainGymQuery,
	useAddGymEquipmentMutation,
	useEditGymEquipmentMutation,
} from "store/api/api";
import { useParams } from "react-router-dom";
import InputField from "components/UI/Form/InputField";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";
import { equipmentStatuses } from "helpers/constants";
import "./styles.scss";
import { required } from "helpers/Form/commonFormValidations";

const { useBreakpoint } = Grid;

const Equipment = () => {
	const screens = useBreakpoint();
	const { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const domainGymId = domainGymResponse.data?.id;
	const [toEdit, setToEdit] = useState(null);
	const primaryColor = usePrimaryColor();
	const {
		data: equipments,
		isLoading,
		refetch,
	} = useGetGymEquipmentQuery(domainGymId, {
		skip: !domainGymId,
	});
	const [addGymEquipmentTrigger, addGymEquipment] = useAddGymEquipmentMutation();
	const [editGymEquipmentTrigger, editGymEquipment] = useEditGymEquipmentMutation();
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [modal, contextHolder] = Modal.useModal();
	const [form] = Form.useForm();

	const showModal = () => {
		setIsModalVisible(true);
	};

	const handleOk = async () => {
		const vals = await form.validateFields();
		try {
			if (toEdit) {
				await editGymEquipmentTrigger({ id: toEdit.id, name: vals.name, gym_id: domainGymId }).unwrap();
			} else {
				await addGymEquipmentTrigger({ name: vals.name, gym_id: domainGymId }).unwrap();
			}

			form.resetFields();
			await refetch();
			setIsModalVisible(false);
			notification.success({
				message: "Success",
				description: `Equipment ${toEdit ? "updated" : "added"} successfully!`,
			});
			setToEdit(null);
		} catch (error) {
			notification.error({
				message: "Error",
				description: error.data?.message || "Failed to add equipment",
			});
		}
	};

	const handleCancel = () => {
		form.resetFields();
		setIsModalVisible(false);
		setToEdit(null);
	};

	const data = equipments?.equipments ?? [];
	const onConfirm = async (record) => {
		try {
			await editGymEquipmentTrigger({
				id: record.id,
				gym_id: domainGymId,
				status: record.status === equipmentStatuses.ACTIVE ? "inactive" : "active",
			}).unwrap();
			notification.success({
				message: "Success",
				description: `Equipment ${record.status === equipmentStatuses.ACTIVE ? "inactive" : "active"} successfully!`,
			});
			await refetch();
		} catch (error) {
			notification.error({
				message: "Error",
				description: error.data?.message || "Failed to inactivate equipment",
			});
		}
	};

	const onActivate = (record) => {
		modal.confirm({
			title: "Confirm",
			content: `Are you sure you want to ${
				record.status === equipmentStatuses.ACTIVE ? "inactivate" : "activate"
			} this equipment?`,
			okText: "Confirm",
			cancelText: "Go Back",
			okButtonProps: {
				className: "primary-filled need-help-btn",
				type: "primary",
			},
			cancelButtonProps: {
				className: "primary-filled need-help-btn",
				type: "default",
			},
			icon: null,
			onOk: () => onConfirm(record),
		});
	};
	const columns = [
		{
			title: "Equipment Name",
			dataIndex: "name",
			key: "name",
			sorter: (a, b) => a.name.localeCompare(b.name),
		},
		{
			title: "Actions",
			key: "actions",
			align: "right",
			render: (_, record) => (
				<Flex justify="flex-end" gap="8px">
					<Button
						size="small"
						style={{ minWidth: "unset", color: primaryColor, borderColor: primaryColor }}
						className="need-help-btn"
						onClick={() => {
							setToEdit(record);
							form.setFieldsValue({ name: record.name });
							showModal();
						}}
						icon={<EditOutlined />}
					>
						{screens.xs ? null : "Edit"}
					</Button>

					<Button
						size="small"
						style={{ minWidth: "unset", color: primaryColor, borderColor: primaryColor }}
						className="need-help-btn"
						onClick={() => onActivate(record)}
						icon={<CloseCircleOutlined />}
					>
						{screens.xs ? null : record.status === equipmentStatuses.ACTIVE ? "Inactivate" : "Activate"}
					</Button>
				</Flex>
			),
		},
	];

	const items = [
		{
			key: "1",
			label: "Active",
			children: (
				<Table
					loading={isLoading}
					columns={columns}
					dataSource={data?.filter(({ status }) => status === equipmentStatuses.ACTIVE)}
				/>
			),
		},
		{
			key: "2",
			label: "Inactive",
			children: (
				<Table
					loading={isLoading}
					columns={columns}
					dataSource={data?.filter(({ status }) => status === equipmentStatuses.INACTIVE)}
				/>
			),
		},
	];
	return (
		<Flex vertical gap="20px" style={{ padding: 40 }}>
			<Flex vertical={screens.xs} gap={16} align={screens.xs ? "stretch" : "end"}>
				<Flex flex={1}>
					<Typography.Title style={{ margin: 0 }} level={3}>
						Equipment
					</Typography.Title>
				</Flex>
				<Flex flex={1} justify="end">
					<Button size="small" onClick={showModal} type="primary" style={{ padding: "4px 16px" }}>
						<PlusOutlined />
						Create Equipment
					</Button>
				</Flex>
				<Modal
					open={isModalVisible}
					onOk={handleOk}
					onCancel={handleCancel}
					okText="Add"
					cancelText="Cancel"
					footer={null}
				>
					<Typography.Title level={3} style={{ marginBottom: 16 }}>
						{toEdit ? "Edit Equipment" : "Add New Equipment"}
					</Typography.Title>
					<Form className="equipments" form={form} layout="vertical">
						<Form.Item
							label="Equipment Name"
							name="name"
							rules={[
								{
									required: true,
									validator: (_, value) => {
										const validationStatus = required(value, 255);
										if (validationStatus) {
											return Promise.reject(new Error(validationStatus));
										}
										if (value && value.length > 255) {
											return Promise.reject(new Error("Maximum allowed characters is 255"));
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<InputField placeholder="Enter equipment name" />
						</Form.Item>
						<Flex className="sb-confirm-form__actions" justify="space-between" style={{ marginTop: 32 }}>
							<Button htmlType="button" onClick={handleCancel}>
								Cancel
							</Button>
							<Button
								onClick={handleOk}
								loading={addGymEquipment.isLoading || editGymEquipment.isLoading}
								type="primary"
								variant="filled"
								htmlType="submit"
							>
								{toEdit ? "Save Changes" : "Create Equipment"}
							</Button>
						</Flex>
					</Form>
				</Modal>
			</Flex>
			<Tabs defaultActiveKey="1" items={items} />
			{contextHolder}
		</Flex>
	);
};

export default Equipment;

